import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, type ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type { components } from "@/api/schema";

type S = components["schemas"];
export type ApiToken = S["ApiTokenOut"];

export function useApiTokens(): UseQueryResult<ApiToken[], ApiError> {
  return useQuery<ApiToken[], ApiError>({
    queryKey: queryKeys.apiTokens,
    queryFn: async () => unwrap(await api.GET("/account/api-tokens")),
  });
}

export function useCreateApiToken() {
  const qc = useQueryClient();
  return useMutation<S["ApiTokenCreated"], ApiError, S["ApiTokenCreate"]>({
    mutationFn: async (body) => unwrap(await api.POST("/account/api-tokens", { body })),
    // The result carries the one-time secret: do not keep it in the mutation cache.
    gcTime: 0,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.apiTokens }),
  });
}

export function useRevokeApiToken() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (id) => {
      unwrap(await api.DELETE("/account/api-tokens/{token_id}", { params: { path: { token_id: id } } }));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.apiTokens }),
  });
}
